import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { T } from "@/constants/testIds";
import { BIBLE_TRANSLATIONS } from "@/lib/i18n";

const EXCERPTS = {
  KJV: {
    reference: "1 Timothy 2:3–6",
    text:
      "For this is good and acceptable in the sight of God our Saviour; who will have all men to be saved, and to come unto the knowledge of the truth. For there is one God, and one mediator between God and men, the man Christ Jesus; who gave himself a ransom for all, to be testified in due time.",
    isFull: true,
    attribution: "King James Version — public domain.",
  },
  NIV: {
    reference: "1 Timothy 2:3–6",
    text: "“This is good, and pleases God our Saviour, who wants all people to be saved …”",
    isFull: false,
    attribution: "New International Version — short excerpt shown under fair-quotation guidelines. Read the full passage on an authorised Bible provider.",
    external: "https://www.biblegateway.com/passage/?search=1%20Timothy%202%3A3-6&version=NIV",
  },
  ESV: {
    reference: "1 Timothy 2:3–6",
    text: "“God our Saviour … desires all people to be saved and to come to the knowledge of the truth.”",
    isFull: false,
    attribution: "English Standard Version — short excerpt shown under fair-quotation guidelines. Read the full passage on an authorised Bible provider.",
    external: "https://www.biblegateway.com/passage/?search=1%20Timothy%202%3A3-6&version=ESV",
  },
  NKJV: {
    reference: "1 Timothy 2:3–6",
    text: "“God our Saviour … desires all men to be saved and to come to the knowledge of the truth.”",
    isFull: false,
    attribution: "New King James Version — short excerpt shown under fair-quotation guidelines. Read the full passage on an authorised Bible provider.",
    external: "https://www.biblegateway.com/passage/?search=1%20Timothy%202%3A3-6&version=NKJV",
  },
};

export default function ScriptureFeature() {
  const [tab, setTab] = useState("ESV");
  const current = EXCERPTS[tab];

  return (
    <section
      data-testid={T.scriptureFeature}
      className="bg-subtle border-y border-border py-20 sm:py-28"
    >
      <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <span className="eyebrow">A word for you today</span>
          <h2 className="heading-2 mt-4">
            God <em className="italic text-forest">desires</em> that you be saved.
          </h2>
          <p className="body-copy mt-5 text-ink-muted">
            The apostle Paul writes to Timothy about the heart of the Gospel: one God, one mediator, and a Saviour who
            gave Himself for all. Read the passage, choose your translation, and consider its invitation.
          </p>
        </div>

        <div className="md:col-span-8">
          <div className="paper-card p-8 sm:p-12 relative">
            <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
              <span className="eyebrow">Scripture · {current.reference}</span>
              <div className="flex items-center gap-1">
                {BIBLE_TRANSLATIONS.map((t) => (
                  <button
                    key={t.code}
                    data-testid={T.scriptureTab(t.code)}
                    onClick={() => setTab(t.code)}
                    aria-pressed={tab === t.code}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${
                      tab === t.code
                        ? "bg-ink text-background"
                        : "text-ink-muted hover:text-ink"
                    }`}
                  >
                    {t.code}
                  </button>
                ))}
              </div>
            </div>

            <blockquote className="font-serif text-2xl sm:text-3xl italic leading-snug text-ink" style={{ fontWeight: 400 }}>
              {current.text}
            </blockquote>

            <div className="mt-8 border-t border-border pt-6">
              <p className="text-xs text-ink-muted leading-relaxed">{current.attribution}</p>
              {current.external && (
                <a
                  href={current.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-sm text-forest hover:underline underline-offset-4"
                >
                  Read the full passage on an authorised Bible provider
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
              <div>
                <p className="eyebrow">The Author</p>
                <p className="mt-2 text-ink-soft">Paul, writing to Timothy — pastoral, urgent, tender.</p>
              </div>
              <div>
                <p className="eyebrow">The Setting</p>
                <p className="mt-2 text-ink-soft">A young church in Ephesus, learning what public worship should look like.</p>
              </div>
              <div>
                <p className="eyebrow">The Gospel</p>
                <p className="mt-2 text-ink-soft">One God. One Mediator. One ransom given for all.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
